import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import FormInputNumber from "../common/FormInputNumber";
import e3dModel from "../../model/e3dModel";
import AppSubSections from "../../utils/AppSubSections";
import FormInputDate from "../common/FormInputDate";
import UploadManager from "../../shared/UploadManager";
import FormDropDown from "../common/FormDropDown";
import UnitScale from "../../model/enums/UnitScale";
import MeasurementUnitsUtil from "../../utils/MeasurementUnitsUtil";
import AppIcons from "../../utils/AppIcons";
import { Tooltip } from "react-tooltip";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";

interface Ie3dFormErrors {
  name: string;
  description: string;
  width: string;
  length: string;
  height: string;
  scale: string;
  reference_date: string;
}

const defaultFormErrors: Ie3dFormErrors = {
  name: "",
  description: "",
  width: "",
  length: "",
  height: "",
  scale: "",
  reference_date: "",
};

function E3dAddEdit() {
  // read the IDs from the url
  const { chid, id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<e3dModel>(e3dModel.EmptyInstance());
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<Ie3dFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));

  // user agreement to terms and conditions
  const [agreeTerms, setAgreeTerms] = useState(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let e3d: e3dModel | undefined = undefined;
        if (id) {
          e3d = await apiClient?.get<e3dModel>({
            url: APIEndPoints.E3DS.ONE(parseInt(id)),
          });
        }

        // unify initial model with data from all sources
        const currentData = e3d ? e3d : e3dModel.EmptyInstance();
        if (chid) {
          currentData.id_chelement = parseInt(chid);
        }

        // set into the state all the information
        setFormData(currentData);
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, chid, id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    const errors = validateSiteForm(formData);

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    try {
      // upload the new icon, if it exists (situation managed by the UploadManager)
      const uploadResult = await uploadManager.uploadFiles();
      console.log(uploadResult);
      if (uploadResult instanceof Array) {
        iconServerId = uploadResult[0].serverId;
      }
    } catch (exception) {
      console.log(exception);
    }

    var body = new e3dModel(
      authManager.getUserID()!!,
      formData.name,
      formData.description,
      formData.id_chelement,
      iconServerId,
      formData.width,
      formData.length,
      formData.height,
      formData.scale,
      formData.reference_date,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id!);
      apiClient
        ?.put({
          url: APIEndPoints.E3DS.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Record updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.E3DS,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    } else {
      apiClient
        ?.post<e3dModel>({
          url: APIEndPoints.E3DS.NEW(body.id_chelement),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Record created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.E3DS,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.E3DS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record deleted successfully",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections.CHELEMENTS,
            id: formData.id_chelement,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete this record",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as e3dModel);
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.go_back({
        section: AppSections.CHELEMENTS,
        id: formData.id_chelement,
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = id
    ? "Edit Condition Assessment"
    : "Add Condition Assessment";
  const confirmLabel = id
    ? "Edit Condition Assessment"
    : "Add Condition Assessment";

  const deleteButton = id ? (
    <Button
      label="Delete this record"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this record?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  const unitOptions: { label: string; value: string }[] = [];
  Object.entries(UnitScale).map(([key, value]) => {
    if (isNaN(parseInt(key))) {
      unitOptions.push({
        label: key.replace(/_/g, " "),
        value: value.toString(),
      });
    }
    return null;
  });

  const unitShort = MeasurementUnitsUtil.short(formData.scale);

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
    >
      <FormInputDate
        label="Reference date"
        id="reference_date"
        value={formData.reference_date}
        onChange={formInputChange}
        error={formErrors.reference_date}
        required
      />

      <FormInputText
        label="Name"
        id="name"
        value={formData.name}
        required={true}
        placeholder=""
        onChange={formInputChange}
        error={formErrors.name}
        hint="Please provide a human-friendly identifier relevant for this assessment. For example 'Condition assessment of the south wing of Schönbrunn Palace'."
      />

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={true}
        onChange={formInputChange}
        error={formErrors.description}
        hint="Describe succintly the main points of this assessment (purpose, approach used,...), so you can easilly differentiate assessments over time."
      />

      <div>
        <label className="form-label">
          Details on the attached e3D*{" "}
          <span className="tooltip-hint">
            <a data-tooltip-id="e3d-tooltip" data-tooltip-place="right">
              {AppIcons.INFO}
            </a>
            <Tooltip id="e3d-tooltip">
              Refers to a 3D representation of a cultural heritage asset
              <br />
              created by combining multiple scans performed with different
              <br />
              sensors, enriched with various data that represent the state of
              <br />
              the asset at a specific moment in time.
            </Tooltip>
          </span>
        </label>
        <div className="form-input-group-vertical p-4">
          <FormDropDown
            label="Unit"
            id="scale"
            value={formData.scale}
            required={true}
            onChange={formInputChange}
            options={unitOptions}
            error={formErrors.scale}
          />

          <div className="flex w-full items-stretch mt-6">
            <div className="flex-grow mr-4">
              <FormInputNumber
                label={"Width (" + unitShort + ")"}
                id="width"
                value={formData.width}
                required={true}
                onChange={formInputChange}
                error={formErrors.width}
              />
            </div>
            <div className="flex-grow mr-4">
              <FormInputNumber
                label={"Length (" + unitShort + ")"}
                id="length"
                value={formData.length}
                required={true}
                onChange={formInputChange}
                error={formErrors.length}
              />
            </div>
            <div className="flex-grow mr-4">
              <FormInputNumber
                label={"Height (" + unitShort + ")"}
                id="height"
                value={formData.height}
                required={true}
                onChange={formInputChange}
                error={formErrors.height}
              />
            </div>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateSiteForm(formData: e3dModel): Ie3dFormErrors {
  let errors: Ie3dFormErrors = {
    name: "",
    description: "",
    width: "",
    length: "",
    height: "",
    scale: "",
    reference_date: "",
  };

  errors.name =
    formData.name.length < 4
      ? "E3D name should be at least 4 characters long"
      : "";
  errors.description =
    formData.description.length < 10
      ? "Description should be at least 10 characters long"
      : "";
  errors.width =
    formData.width <= 0 ? "Width should be at a positive number" : "";
  errors.length =
    formData.length <= 0 ? "Length should be at a positive number" : "";
  errors.height =
    formData.height <= 0 ? "Height should be at a positive number" : "";
  errors.scale =
    formData.scale <= 0 ? "Scale should be at a positive number" : "";
  errors.reference_date =
    formData.reference_date.length === 0 ? "Reference date should be set" : "";
  return errors;
}

function isFormInvalid(errors: Ie3dFormErrors): boolean {
  return (
    errors.name !== "" ||
    errors.description !== "" ||
    errors.width !== "" ||
    errors.length !== "" ||
    errors.height !== "" ||
    errors.scale !== "" ||
    errors.reference_date !== ""
  );
}

export default E3dAddEdit;
